import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { Auth } from 'aws-amplify';

const generateUniqueId = () => {
  return `${uuidv4()}_${Date.now()}`;
};

const initialState = {
  composerCarts: {},
  vendorInfo: {},
  isLoading: false,
  error: null,
  itemsNotFound: [],
  optimizationMetadata: null,
  messageId: null,
  // Add tracking for undo functionality
  previousState: null,
  canUndo: false,
  // Operation tracking fields for cart sync
  operations: [],
  pendingSync: false,
  syncError: null,
  syncSuccessOpen: false,
  syncErrorOpen: false,
};

// Async thunk for starting the preview carts action
export const startPreviewCarts = createAsyncThunk(
  'composer/startPreviewCarts',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const messageId = uuidv4();
      dispatch(setMessageId(messageId));
      
      // We don't directly return anything from this thunk
      // Instead we dispatch a websocket action that will be handled by the middleware
      return messageId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendPreviewCartsAction = (payload) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      
      const messageId = state.composer.messageId || uuidv4();
      
      // Get current user session to retrieve the token
      const session = await Auth.currentSession();
      const token = session.getAccessToken().getJwtToken();
      
      // Create the websocket payload
      const wsPayload = {
        action: "preview_carts",
        message_id: messageId,
        authorization: token,
        ...payload
      };
      
      // Set loading state
      dispatch(setLoading(true));
      
      // Dispatch the websocket action to the middleware
      dispatch({
        type: "DGI_API_WEBSOCKET_SEND",
        payload: wsPayload
      });
      
    } catch (error) {
      dispatch(setError(error.message));
      dispatch(setLoading(false));
    }
  };
};

export const composerSlice = createSlice({
  name: 'composer',
  initialState,
  reducers: {
    // Save the current state before making changes (for undo functionality)
    savePreviousState: (state) => {
      // Deep clone the current cart state
      state.previousState = JSON.parse(JSON.stringify(state.composerCarts));
      state.canUndo = true;
    },
    
    // Undo the last action by restoring the previous state
    undoLastAction: (state) => {
      if (state.previousState && state.canUndo) {
        // First apply assignItemIdsAndCreateOperations to generate new operations
        const cartItemsWithUniqueIDs = assignItemIdsAndCreateOperations(state.previousState, state.operations);
        state.composerCarts = cartItemsWithUniqueIDs;
        
        // Clear the previous state to prevent multiple undos
        state.previousState = null;
        state.canUndo = false;
      }
    },
    
    // Set message ID for tracking websocket responses
    setMessageId: (state, action) => {
      state.messageId = action.payload;
    },
    
    // Handle preview carts success
    previewCartsSuccess: (state, action) => {
      const { vendor_po_items, items_not_found, optimization_metadata } = action.payload;
      // Process vendor items and update operations
      if (vendor_po_items) {
        assignItemIdsAndCreateOperations(vendor_po_items, state.operations);
      }
      
      // Update state with the preview cart data AFTER assigning composer_item_id
      state.composerCarts = vendor_po_items || {};
      
      state.itemsNotFound = items_not_found || [];
      state.optimizationMetadata = optimization_metadata || null;
      state.isLoading = false;
      state.error = null;
      
      // Reset undo capability when loading new data
      state.previousState = null;
      state.canUndo = false;
      
      // Log for debugging
      
      // Extract vendor info from the response
      const vendorInfo = {};
      if (vendor_po_items) {
        Object.entries(vendor_po_items).forEach(([vendorCode, data]) => {
          vendorInfo[vendorCode] = {
            name: data.name || vendorCode,
            shippingThreshold: optimization_metadata?.shipping_thresholds?.[vendorCode] || 0,
            currentTotal: data.totalPrice || 0,
            shippingCost: optimization_metadata?.shipping_costs?.[vendorCode] || 0
          };
        });
      }
      
      state.vendorInfo = vendorInfo;
    },
    
    // Handle preview carts error
    previewCartsError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    resetPreviewCartsError: (state) => {
      state.error = null;
    },
    
    // Add items to a vendor's cart
    addItemsToCart: (state, action) => {
      const { vendorCode, items } = action.payload;
      
      // Initialize vendor array if it doesn't exist
      if (!state.composerCarts[vendorCode]) {
        state.composerCarts[vendorCode] = [];
      }
      
      // Add the items to the vendor's array
      state.composerCarts[vendorCode] = [
        ...state.composerCarts[vendorCode],
        ...items
      ];
    },
    
    // Remove a specific item from a cart
    removeItemFromCart: (state, action) => {
      const { vendorCode, composerItemId } = action.payload;
      
      // Remove the operation for this item
      state.operations = state.operations.filter(op => op.composer_item_id !== composerItemId);
      
      // Rest of existing removeItemFromCart logic...
      if (state.composerCarts[vendorCode]) {
        // Filter out the item with the matching composer_item_id
        state.composerCarts[vendorCode] = {
          ...state.composerCarts[vendorCode],
          items: state.composerCarts[vendorCode]["items"].filter(item => {
            return item.composer_item_id !== composerItemId
          })
        };
        
        // If the vendor array is now empty, remove the vendor
        if (state.composerCarts[vendorCode].items.length === 0) {
          delete state.composerCarts[vendorCode];
        }
      }
    },
    
    // Switch an item from one vendor to another in the composer
    switchComposerItem: (state, action) => {
      const { 
        sourceVendorCode, 
        composerItemId, 
        destVendorCode, 
        destItem,
        destWarehouse,
        destPrice,
        lowestPrice,
        priceDifference
      } = action.payload;
      
      // Initialize source vendor cart if it doesn't exist
      if (!state.composerCarts[sourceVendorCode]) {
        state.composerCarts[sourceVendorCode] = {
          items: [],
          name: sourceVendorCode,
          cart_id: `${sourceVendorCode}-preview-cart`,
          totalPrice: 0,
          totalQuantity: 0,
          vendor_code: sourceVendorCode
        };
      }

      // Initialize destination vendor cart if it doesn't exist
      if (!state.composerCarts[destVendorCode]) {
        state.composerCarts[destVendorCode] = {
          items: [],
          name: destVendorCode,
          cart_id: `${destVendorCode}-preview-cart`,
          totalPrice: 0,
          totalQuantity: 0,
          vendor_code: destVendorCode
        };
      }

      // Now we can safely work with the items arrays
      let sourceItem = null;
      let sourceItemIndex = -1;

      // Find the item with the matching composer_item_id
      sourceItemIndex = state.composerCarts[sourceVendorCode].items.findIndex(item => {
        const match = item.composer_item_id === composerItemId;
        return match;
      });
      
      if (sourceItemIndex !== -1) {
        // Get the source item
        sourceItem = {...state.composerCarts[sourceVendorCode].items[sourceItemIndex]};
        
        // Remove the item from the source
        state.composerCarts[sourceVendorCode].items.splice(sourceItemIndex, 1);
        
        // Update source cart totals
        state.composerCarts[sourceVendorCode].totalPrice -= sourceItem.totalPrice;
        state.composerCarts[sourceVendorCode].totalQuantity -= sourceItem.quantity;
        
        // Remove empty vendor cart
        if (state.composerCarts[sourceVendorCode].items.length === 0 && sourceVendorCode !== destVendorCode) {
          delete state.composerCarts[sourceVendorCode];
        }
      }
      
      if (sourceItem) {
        // Handle price formatting - remove currency symbol and convert to number
        let validDestPrice;
        if (typeof destPrice === 'string') {
          validDestPrice = Number(destPrice.replace(/[^0-9.]/g, ''));
        } else {
          validDestPrice = Number(destPrice);
        }
        
        // If still not a valid number, use fallback
        if (isNaN(validDestPrice)) {
          validDestPrice = isNaN(Number(sourceItem.unitPrice)) ? 0 : Number(sourceItem.unitPrice);
        }
        
        // Calculate total price
        const totalPrice = validDestPrice * sourceItem.quantity;
        
        // Create formatted price string ensuring it has 2 decimal places
        const priceString = validDestPrice.toFixed(2);
        
        // Ensure priceDifference is a valid number (defensive coding)
        let validPriceDifference = priceDifference;
        if (priceDifference === null || priceDifference === undefined || isNaN(priceDifference)) {
          validPriceDifference = 0;
        }

        // Create the destination item
        const newItem = {
          ...sourceItem,
          style: destItem.style,
          size: destItem.size,
          itemNumber: destItem.itemNumber,
          vendorCode: destVendorCode,
          warehouse: destWarehouse.name,
          warehouseCode: destWarehouse.warehouseCode || "",
          unitPrice: validDestPrice,
          price: priceString,
          totalPrice: totalPrice,
          lowest_price: Boolean(lowestPrice),
          price_difference: validPriceDifference,
          from_actual_cart: false,
          is_fixed_position: false
        };
        
        // Update operation
        const operationIndex = state.operations.findIndex(
          op => op.composer_item_id === sourceItem.composer_item_id
        );
        
        if (operationIndex !== -1) {
          state.operations[operationIndex] = {
            ...state.operations[operationIndex],
            operation_type: 'ADD',
            vendorCode: destVendorCode,
            warehouse: destWarehouse.name,
            warehouseCode: destWarehouse.warehouseCode || "",
            size: destItem.size,
            itemNumber: destItem.itemNumber,
            unitPrice: validDestPrice,
            price: priceString,
            totalPrice: totalPrice,
            lowest_price: Boolean(lowestPrice),
            price_difference: validPriceDifference
          };
        }
        
        // Add the item to the destination cart
        state.composerCarts[destVendorCode].items.push(newItem);
        
        // Update destination cart totals
        state.composerCarts[destVendorCode].totalPrice += totalPrice;
        state.composerCarts[destVendorCode].totalQuantity += newItem.quantity;
      }
    },
    
    // Remove all items for a specific invoice by vendor
    removeInvoiceItemsByVendor: (state, action) => {
      const { invoiceNumber, vendorCode } = action.payload;
      if (state.composerCarts[vendorCode]?.items) {
        state.composerCarts[vendorCode].items = state.composerCarts[vendorCode].items
          .filter(item => {
            if (invoiceNumber === "Unassigned Items") {
              // Remove items with no invoice_number AND not from actual cart
              return !!item.invoice_number || item.from_actual_cart;
            }
            // Remove items matching the given invoice number
            return item.invoice_number !== invoiceNumber;
          });
        if (state.composerCarts[vendorCode].items.length === 0) {
          delete state.composerCarts[vendorCode];
        }
      }
    },

    // Remove all items for a specific invoice
    removeInvoiceItems: (state, action) => {
      const { invoiceNumber } = action.payload;
      Object.entries(state.composerCarts).forEach(([vendorCode, vendorInfo]) => {
        // Check if vendor exists and has items
        if (state.composerCarts[vendorCode]?.items) {
          // Filter out items with matching invoice number
          state.composerCarts[vendorCode].items = state.composerCarts[vendorCode].items
            .filter(item => {
              return item.invoice_id !== invoiceNumber;
            });
          
          // If the items array is now empty, remove the vendor
          if (state.composerCarts[vendorCode].items.length === 0) {
            delete state.composerCarts[vendorCode];
          }
        }
      });
    },
    
    // Remove all items for a specific vendor
    removeVendorItems: (state, action) => {
      const { vendorCode } = action.payload;
      
      // Check if the vendor exists
      if (state.composerCarts[vendorCode]) {
        const onlyExistingCartItems = state.composerCarts[vendorCode].items.filter(item => item.from_actual_cart)
        if (onlyExistingCartItems.length > 0) {
          state.composerCarts[vendorCode] = {
            ...state.composerCarts[vendorCode],
            items: onlyExistingCartItems
          };
        } else {
          // Remove the entire vendor entry
          delete state.composerCarts[vendorCode];
        }
      }
    },
    
    // Clear all composer carts
    clearComposerCarts: (state) => {
      state.composerCarts = {};
    },
    
    // Set loading state
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Set vendor info
    setVendorInfo: (state, action) => {
      state.vendorInfo = action.payload;
    },

    // Start sync process
    syncStart: (state) => {
      state.pendingSync = true;
      state.syncError = null;
    },

    // Handle sync success
    syncSuccess: (state) => {
      state.operations = [];
      state.pendingSync = false;
      state.syncError = null;
      state.composerCarts = [];
      state.syncSuccessOpen = true;

      // Clear undo state since we've committed the changes
      state.previousState = null;
      state.canUndo = false;
    },

    // Handle sync failure
    syncFailure: (state, action) => {
      state.pendingSync = false;
      state.syncError = action.payload;
      state.syncErrorOpen = true;
    },

    // Clear operations
    clearOperations: (state) => {
      state.operations = [];
    },

    clearItemsNotFound: (state) => {
      state.itemsNotFound = [];
    },

    closeSyncSuccess: (state) => {
      state.syncSuccessOpen = false;
    },

    closeSyncError: (state) => {
      state.syncErrorOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startPreviewCarts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startPreviewCarts.fulfilled, (state) => {
        // The actual data will be handled by the previewCartsSuccess reducer
        // when the websocket response is received
      })
      .addCase(startPreviewCarts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addDefaultCase((state, action) => {
        // Handle the websocket message with the preview carts response
        if (action.type === 'DGI_API_WEBSOCKET_MESSAGE') {
          const payload = action.payload;
          if (payload.action === 'preview_carts' && payload.message_id === state.messageId) {
            if (payload.error) {
              state.error = payload.error;
              state.isLoading = false;
            } else {
              // Handle the successful preview carts response
              const { vendor_po_items, items_not_found, optimization_metadata } = payload;
          
              // Transform the incoming vendor_po_items to the right format
              const incomingItems = vendor_po_items ? 
                Object.fromEntries(
                  Object.entries(vendor_po_items).map(([vendorCode, data]) => [
                    vendorCode,
                    data.items
                  ])
                ) 
                : {};
              
              // Process vendor items and update operations
              if (incomingItems) {
                assignItemIdsAndCreateOperations(incomingItems, state.operations);
              }
              
              // CHANGED: Instead of merging, we now completely replace the state with the optimized result
              // This aligns with the backend's new global optimization approach
              // First, save the current state for potential undo
              state.previousState = JSON.parse(JSON.stringify(state.composerCarts));
              state.canUndo = true;
              
              // Then replace the state entirely with the optimized result from the backend
              if (vendor_po_items) {
                state.composerCarts = incomingItems;
              }
              
              state.itemsNotFound = items_not_found || [];
              state.optimizationMetadata = optimization_metadata || null;
              state.isLoading = false;
              state.error = null;
              
              // Extract vendor info from the response and completely replace existing vendor info
              const newVendorInfo = {};
              if (vendor_po_items) {
                Object.entries(vendor_po_items).forEach(([vendorCode, data]) => {
                  newVendorInfo[vendorCode] = {
                    name: data.name || vendorCode,
                    shippingThreshold: optimization_metadata?.shipping_thresholds?.[vendorCode] || 0,
                    currentTotal: data.totalPrice || 0,
                    shippingCost: optimization_metadata?.shipping_costs?.[vendorCode] || 0
                  };
                });
                
                // Replace vendor info completely instead of merging
                state.vendorInfo = newVendorInfo;
              }
            }
          }
        }
      });
  },
});

// Export actions
export const {
  savePreviousState,
  undoLastAction,
  setMessageId,
  addItemsToCart,
  removeItemFromCart,
  switchComposerItem,
  removeInvoiceItemsByVendor,
  removeInvoiceItems,
  removeVendorItems,
  clearComposerCarts,
  setLoading,
  setError,
  setVendorInfo,
  previewCartsSuccess,
  previewCartsError,
  resetPreviewCartsError,
  syncStart,
  clearItemsNotFound,
  closeSyncSuccess,
  closeSyncError,
} = composerSlice.actions;

// Basic selectors
export const selectAllComposerCarts = (state) => state.composer.composerCarts;
export const selectAllOperations = (state) => state.composer.operations;
export const selectIsLoading = (state) => state.composer.isLoading;
export const selectError = (state) => state.composer.error;
export const selectVendorInfo = (state) => state.composer.vendorInfo;
export const selectComposerItemsNotFound = (state) => state.composer.itemsNotFound;
export const selectOptimizationMetadata = (state) => state.composer.optimizationMetadata;
export const selectMessageId = (state) => state.composer.messageId;
export const selectCanUndo = (state) => state.composer.canUndo;
export const selectPendingSync = (state) => state.composer.pendingSync;
export const selectSyncSuccessOpen = (state) => state.composer.syncSuccessOpen;
export const selectSyncErrorOpen = (state) => state.composer.syncErrorOpen;

// New selector to check if all items are from actual carts
export const selectAllItemsAreFromActualCart = createSelector(
  [selectAllComposerCarts],
  (carts) => {
    // Early return if no carts
    if (!carts || Object.keys(carts).length === 0) return false;
    
    // Check if there are any items at all
    let hasAnyItems = false;
    
    // Check each vendor's items
    for (const vendorCode of Object.keys(carts)) {
      const vendorData = carts[vendorCode];
      const items = vendorData?.items || [];
      
      if (items.length > 0) {
        hasAnyItems = true;
        // If any item is NOT from_actual_cart, return false
        if (items.some(item => !item.from_actual_cart)) {
          return false;
        }
      }
    }
    
    // If we have items and all are from_actual_cart, return true
    return hasAnyItems;
  }
);

// Memoized selectors
export const selectComposerCartByVendor = createSelector(
  [selectAllComposerCarts, (_, vendorCode) => vendorCode],
  (carts, vendorCode) => carts[vendorCode] || []
);

// Helper selectors
export const selectVendorsWithCarts = createSelector(
  [selectAllComposerCarts, selectVendorInfo],
  (carts, vendorInfo) => {
    return Object.entries(carts).map(([vendorCode, vendorData]) => {
      // Check if vendorData is directly an array or has an items array property
      const items = Array.isArray(vendorData) ? vendorData : (vendorData.items || []);
      
      // Group items by invoice for UI display
      const invoiceGroups = items.reduce((groups, item) => {
        const invoice = item.invoice_number;
        if (!groups[invoice]) {
          groups[invoice] = [];
        }
        groups[invoice].push(item);
        return groups;
      }, {});
      
      // Convert invoice groups to the format expected by the UI
      const invoices = Object.entries(invoiceGroups).map(([invoiceNumber, invoiceItems]) => ({
        invoiceNumber,
        invoiceItems,
        totalPrice: invoiceItems.reduce((sum, item) => sum + item.totalPrice, 0),
        invoiceNickname: invoiceItems[0]?.invoice_nickname || ""
      }));
      
      // Get vendor name - either from vendorData, vendorInfo, or fallback to vendorCode
      const vendorName = vendorData.name || vendorInfo[vendorCode]?.name || vendorCode;
      
      return [
        vendorCode, 
        {
          name: vendorName,
          invoices
        }
      ];
    });
  }
);

// Add a selector to get composer carts in a format similar to regular carts for compatibility
export const selectComposerCartsFormatted = createSelector(
  [selectAllComposerCarts, selectVendorInfo, (_, vendorCodes) => vendorCodes],
  (carts, vendorInfo, vendorCodes) => {
    if (!vendorCodes || vendorCodes.length === 0) return {};
    
    const result = {};
    vendorCodes.forEach(vendorCode => {
      const vendorData = carts[vendorCode];
      if (!vendorData) return;
      
      // Check if vendorData is directly an array or has an items array property
      const items = Array.isArray(vendorData) ? vendorData : (vendorData.items || []);
      
      if (items && items.length > 0) {
        // If vendorData already has totalPrice, use it, otherwise calculate
        const totalPrice = vendorData.totalPrice || 
                           items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
        
        // Use name from vendorData if available
        const name = vendorData.name || vendorInfo[vendorCode]?.name || vendorCode;
        
        result[vendorCode] = {
          items,
          totalPrice,
          name
        };
      }
    });
    
    return result;
  }
);

// Selector to check if an invoice ID exists in any vendor cart
export const selectInvoiceExistsInComposer = createSelector(
  [selectAllComposerCarts, (_, invoiceId) => invoiceId],
  (carts, invoiceId) => {
    // Early return if carts is empty
    if (!carts || Object.keys(carts).length === 0) return false;
    
    // Check each vendor
    for (const vendorCode of Object.keys(carts)) {
      const vendorData = carts[vendorCode];
      
      // Ensure vendorData is an array and check if any item has the matching invoice_id
      if (Array.isArray(vendorData?.items) && 
          vendorData?.items?.some(item => item.invoice_id === invoiceId)) {
        return true;
      }
    }
    
    // Invoice not found in any vendor cart
    return false;
  }
);

// Helper function to assign composer_item_ids and create operations
const assignItemIdsAndCreateOperations = (vendorPoItems, operations = []) => {
  operations.length = 0;
  
  Object.entries(vendorPoItems).forEach(([vendorCode, vendorData]) => {
    if (vendorData?.items && Array.isArray(vendorData.items)) {
      vendorData.items.forEach(item => {
        // Use new generateUniqueId function
        const composer_item_id = item.composer_item_id || generateUniqueId();
        
        item.composer_item_id = composer_item_id;
        
        operations.push({
          ...item,
          operation_type: 'ADD',
          composer_item_id,
          vendorCode
        });
      });
    }
  });
  
  return vendorPoItems;
};

export default composerSlice.reducer;
